/**
 * `lazyNamed` — React.lazy for a named export, tolerant of stale-deploy reload.
 *
 * Two recovery paths exist for chunk-skew after a deploy:
 *
 * 1. **Resolved `undefined`** — `installPreloadErrorReload` (src/api/clientErrors.ts)
 *    handles `vite:preloadError` by calling `event.preventDefault()` +
 *    `location.reload()`. Suppressing Vite's default rethrow makes the dynamic
 *    import RESOLVE with `undefined`. We stay suspended (never-resolving promise)
 *    so the route spinner holds until the reload lands.
 *
 * 2. **Rejected import** — the `vite:preloadError` interception does NOT cover
 *    `React.lazy` rejections that arrive as render errors (`source: boundary`).
 *    When the import() itself rejects (e.g. 404 on a deleted chunk), we
 *    self-heal: check the shared recovery-reload stamp and, on the first failure,
 *    record the stamp + reload once. A repeat failure inside the window means
 *    the chunk is genuinely missing, so we rethrow and let the boundary report it.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import {
  PRELOAD_RELOAD_WINDOW_MS,
  readPreloadReloadAt,
  writePreloadReloadAt,
} from '@/lib/preloadReloadStamp';
import { pendingInflight, trackInflight } from '@/lib/inflight';

/**
 * Alias for the unified in-flight registry (#490i). Lazy chunk loads are now
 * tracked via `trackInflight` from `src/lib/inflight.ts`; this re-export
 * keeps the existing guard test and the settle hook's import working.
 */
export const pendingLazyLoads: Set<Promise<unknown>> = pendingInflight;

const IMPORT_SPECIFIER = /(?:\bimport|__vi_ssr_dynamic_import__|__vite_ssr_dynamic_import__)\(\s*["'`]([^"'`]+)["'`]/;

/** The module specifier a loader imports, read from its source text; "lazy chunk" if none. */
export function lazyChunkLabel(load: () => unknown, name?: string): string {
  if (name) return name;
  return IMPORT_SPECIFIER.exec(String(load))?.[1] ?? 'lazy chunk';
}

export function lazyNamed<M, P extends object>(
  load: () => Promise<M>,
  pick: (m: M) => ComponentType<P>,
  name?: string,
): LazyExoticComponent<ComponentType<P>> {
  const label = lazyChunkLabel(load, name);
  return lazy(() => {
    const p = load();
    void trackInflight(p, { kind: 'lazy-chunk', label });
    return p.then(
      (m) => {
        if (m === undefined) {
          // Recovery reload in flight (cancelled vite:preloadError). Stay
          // suspended — the route spinner is the right UI until the page
          // replaces itself; rendering nothing/crashing are both wrong.
          return new Promise<never>(() => {});
        }
        return { default: pick(m) };
      },
      (error: unknown) => {
        // The import itself rejected — a 404 on a deleted chunk, a network
        // error, or any other dynamic-import failure. Self-heal with the
        // same one-shot-reload + window strategy as installPreloadErrorReload.
        const now = Date.now();
        const last = readPreloadReloadAt();

        if (last && now - last < PRELOAD_RELOAD_WINDOW_MS) {
          // Already reloaded recently and the chunk is STILL missing.
          // Genuine breakage — let the error propagate to the boundary.
          throw error;
        }

        // First failure this episode: record the stamp and reload.
        writePreloadReloadAt(now);
        window.location.reload();

        // Stay suspended while the reload is in flight — the route spinner
        // is the right UI. This promise intentionally never resolves.
        return new Promise<never>(() => {});
      },
    );
  });
}
